/**
 * Launched GT workspace: live GroundTruth state → the shared review grid.
 *
 * Input is the normalized projection app-backend serves from its last published
 * GT snapshot (services/gtLiveState/gtLiveStateNormalize.js). Output rows use
 * the pre-launch GT grid's column keys, so Overview / Budget / Targeting /
 * Creative / Tracking read the same, but every value comes from GT:
 *
 *   - rows are keyed by the GT ad group id, never position or name (AC-10);
 *   - a field GT does not return is null and renders as unavailable — nothing
 *     is parsed out of names, split from budgets or copied from the IO (AC-12);
 *   - columns with no GT counterpart are dropped from the launched grid rather
 *     than shown with planned numbers (the IO's Impression/Clicks goal and the
 *     creative "Versions" note): a planned value in a live grid would read as
 *     GT's (AC-05);
 *   - a resource GT would not return (an ad group's detail or its creatives,
 *     `readErrors`) leaves its cells blank and flagged as errors — not "—",
 *     which means GT returned no value — so one broken ad group never hides
 *     the rest of the campaign or passes for "no creatives".
 */
import { GT_GROUP_COLS, GT_GROUP_KEYS } from '../CampaignLaunchWorkflow/groundtruth/gtConstants';

/** Keys of pre-launch columns with no GT field behind them. */
const NOT_IN_GT = new Set(['impression_clicks', 'creative_versions']);

/** Column labels that would mislead over GT values. */
const RELABEL = { package: 'Package ID' };

/** The launched grid's column groups: the review grid's, minus planned-only columns, plus GT status. */
export function gtLiveGroupCols() {
  const groups = {};
  for (const key of GT_GROUP_KEYS) {
    groups[key] = (GT_GROUP_COLS[key] || [])
      .filter((c) => !NOT_IN_GT.has(c.k))
      .map((c) => (RELABEL[c.k] ? { ...c, l: RELABEL[c.k] } : c));
  }
  groups.overview = [...groups.overview, { k: 'gt_status', l: 'GT status', w: 110 }];
  return groups;
}

export const GT_LIVE_GROUP_KEYS = GT_GROUP_KEYS;

const MEDIA_LABELS = { app: 'App', web: 'Mobile Web', ctv: 'CTV', audio: 'Audio', dooh: 'DOOH' };
const mediaLabel = (types) => (types?.length ? types.map((t) => MEDIA_LABELS[t] || t).join(', ') : null);
const join = (values, sep = ', ') => {
  const kept = [...new Set((values || []).filter((v) => v != null && v !== ''))];
  return kept.length ? kept.join(sep) : null;
};
const ref = (l) => [l.name, l.id].filter(Boolean).join(' · ');

/** The audiences[] label the GT grid uses everywhere: name · id · type. */
const audienceLabel = (a) => `${a.name || '—'} · ${a.id || '—'} · ${a.type || '—'}${a.exclude ? ' (exclude)' : ''}`;

/** What the ad group targets, in GT's own terms (tactic + its qualifier). */
function targetingSummary(ag) {
  if (ag.onPremiseLocations?.length) {
    return `${ag.tacticLabel} · ${join(ag.onPremiseLocations.map((l) => l.proximity)) || 'proximity unset'}`;
  }
  if (ag.neighborhoodTypes?.length) return `${ag.tacticLabel} · ${ag.neighborhoodTypes.join(', ')}`;
  if (ag.audiences?.length) return `${ag.tacticLabel} · ${join(ag.audiences.map((a) => a.type))}`;
  return ag.tacticLabel || null;
}

const locationFilterLabel = (f) => `${f.exclude ? 'Exclude ' : ''}${f.type || 'Filter'}: ${f.value ?? '—'}${f.radius != null ? ` (${f.radius} mi)` : ''}`;

/** The Location Group this ad group is built on, whichever slot GT carries it in. */
function locationGroups(ag) {
  const groups = [
    ...(ag.onPremiseLocations || []),
    ...(ag.neighborhoods || []),
    ...(ag.audiences || []).filter((a) => a.type === 'location_group'),
  ].filter((l) => l.type === 'location_group');
  return join(groups.map(ref), ' | ');
}

/** Row fields read from the ad group detail (name, status and flight come from GT's ad group list). */
export const DETAIL_FIELDS = [
  'product', 'package', 'package_name', 'device_types', 'device_targeting', 'targeting_product',
  'budget', 'bid_price', 'bid_type', 'billability_type', 'optimization_strategy',
  'audiences', 'audienceList', 'location_targeting', 'location_group', 'radial', 'demographic_targeting',
  'tracking_visits_to', 'drive_to_type',
];
/** Row fields read from the ad group's creative list. */
export const CREATIVE_FIELDS = [
  'creative_sizes', 'creative_type', 'creative_count',
  'placement_320x50', 'placement_300x250', 'placement_320x480', 'placement_id_728x90',
];

const WHAT_UNREAD = { detail: "this ad group's settings", creatives: "this ad group's creatives" };

/** The error a cell carries when GT would not return the resource behind it. */
function unreadAnnotation(kind, err) {
  // The reader's message names the resource and GT's answer (status and GT's own text).
  const what = err?.message
    || `GroundTruth did not return ${WHAT_UNREAD[kind]}${err?.status ? ` (HTTP ${err.status})` : ''}.`;
  return {
    type: 'm',
    unread: true,
    category: 'Not returned by GroundTruth',
    msg: `${what} The value is left blank. Refresh GT data to read it again.`,
  };
}

/** Blank and flag every field of the resources GT did not return. */
function markUnread(row, readErrors) {
  if (!readErrors) return row;
  const annotations = { ...row.annotations };
  const blanked = {};
  for (const [kind, fields] of [['detail', DETAIL_FIELDS], ['creatives', CREATIVE_FIELDS]]) {
    if (!readErrors[kind]) continue;
    const ann = unreadAnnotation(kind, readErrors[kind]);
    for (const f of fields) {
      blanked[f] = null;
      annotations[f] = ann;
    }
  }
  if (readErrors.detail) {
    // Header aliases of detail fields: nothing to show, not a guess.
    Object.assign(blanked, { tactic: null, market: null, cpm: null, audienceEntries: null });
  }
  return { ...row, ...blanked, annotations };
}

/** Third-party placement ids of this ad group's creatives of one size. */
const placementFor = (creatives, size) => join(creatives.filter((c) => c.size === size).map((c) => c.thirdPartyPlacementId));

/**
 * @param {object} data normalized live state ({campaign, adGroups, creatives, associations})
 * @returns {Array<object>} grid rows
 */
export function buildGtLiveRows(data) {
  if (!data?.adGroups) return [];
  const creativeById = new Map((data.creatives || []).map((c) => [c.id, c]));
  return data.adGroups.map((ag, i) => {
    const creatives = (ag.creativeIds || []).map((id) => creativeById.get(id)).filter(Boolean);
    const audiences = (ag.audiences || []).filter((a) => a.type !== 'location_group');
    const demographics = join([
      ag.genders?.length ? `Gender: ${ag.genders.join(', ')}` : null,
      ag.ageRanges?.length ? `Age: ${ag.ageRanges.join(', ')}` : null,
      ag.incomeRanges?.length ? `Income: ${ag.incomeRanges.join(', ')}` : null,
    ], ' · ');
    return markUnread({
      rowId: `gt-live-${ag.id}`,
      adgroup_id: ag.id,
      idx: i + 1,
      name: ag.name || '—',
      channel: 'GT',
      fmt: ag.tacticLabel ? ag.tacticLabel.slice(0, 4).toUpperCase() : 'GT',
      gt_status: ag.statusLabel,
      product: ag.tacticLabel,
      package: ag.adPackage?.id ?? null,
      package_name: ag.adPackage?.name ?? null,
      device_types: mediaLabel(ag.mediaTypes),
      device_targeting: mediaLabel(ag.mediaTypes),
      targeting_product: targetingSummary(ag),
      start_date: ag.startDate,
      end_date: ag.endDate,
      budget: ag.budgetTotal,
      bid_price: ag.bidPrice,
      bid_type: ag.bidType ? ag.bidType.toUpperCase() : null,
      billability_type: ag.billabilityLabel,
      optimization_strategy: ag.optimizationStrategy,
      audiences: audiences.length ? `${audiences.length} audience${audiences.length === 1 ? '' : 's'}` : '—',
      audienceList: audiences.length ? audiences.map(audienceLabel).join(' · ') : '—',
      audienceEntries: audiences.map(audienceLabel),
      location_targeting: ag.locationFilters?.length ? ag.locationFilters.map(locationFilterLabel).join('; ') : null,
      location_filter_count: ag.locationFilters?.length || 0,
      location_group: locationGroups(ag),
      radial: join((ag.onPremiseLocations || []).map((l) => l.radius)),
      demographic_targeting: demographics,
      creative_sizes: join(creatives.map((c) => c.size)),
      creative_type: join(creatives.map((c) => c.type)),
      creative_count: creatives.length,
      placement_320x50: placementFor(creatives, '320x50'),
      placement_300x250: placementFor(creatives, '300x250'),
      placement_320x480: placementFor(creatives, '320x480'),
      placement_id_728x90: placementFor(creatives, '728x90'),
      tracking_visits_to: join((ag.driveToLocations || []).map(ref), ' | '),
      drive_to_type: join((ag.driveToLocations || []).map((l) => l.type)),
      // The shared header cells read these aliases.
      market: ag.locationFilters?.length ? `${ag.locationFilters.length} filters` : '—',
      tactic: ag.tacticLabel || '—',
      start: ag.startDate,
      end: ag.endDate,
      cpm: ag.bidPrice,
      imps: null,
      // Current GT state carries no review findings; historical ones live under Activity (AC-07).
      annotations: {},
    }, ag.readErrors);
  });
}

/** Header tiles: only what was fetched from GT. */
export function gtLiveTiles(data) {
  if (!data) return null;
  return {
    adGroups: data.adGroups?.length ?? 0,
    creatives: data.creatives?.length ?? 0,
    // Ad groups whose creatives GT did not return: the creative count is of what was read.
    creativesUnreadAdGroups: (data.adGroups || []).filter((ag) => ag.readErrors?.creatives).length,
    budget: data.campaign?.budget ?? null,
    startDate: data.campaign?.startDate ?? null,
    endDate: data.campaign?.endDate ?? null,
    status: data.campaign?.statusLabel ?? null,
  };
}
